"use client";

import { useCallback, useState } from "react";
import { BROWSER_TZ, formatTimestamp } from "@/lib/time";

interface SessionConflictDialogProps {
  activeDevice: string;
  lastActivity: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

export function SessionConflictDialog({
  activeDevice,
  lastActivity,
  onConfirm,
  onCancel,
}: SessionConflictDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm]);

  // Pre-auth context — no user.timezone available yet, use browser TZ.
  const formattedTime = formatTimestamp(lastActivity, BROWSER_TZ, "long");

  return (
    <div className="rounded-xl border border-warning bg-surface p-8 shadow-sm">
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-warning-light">
        <svg
          className="h-5 w-5 text-warning"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
          />
        </svg>
      </div>

      <h2 className="mb-2 text-lg font-medium text-text-primary">
        Active session detected
      </h2>
      <p className="mb-4 text-sm text-text-secondary">
        You have an active session on another device. Signing in here will end
        that session.
      </p>

      <div className="mb-6 rounded-lg bg-accent-light p-3">
        <div className="text-sm">
          <span className="font-medium text-text-primary">Device:</span>{" "}
          <span className="text-text-secondary">{activeDevice}</span>
        </div>
        <div className="mt-1 text-sm">
          <span className="font-medium text-text-primary">Last active:</span>{" "}
          <span className="text-text-secondary">{formattedTime}</span>
        </div>
      </div>

      <div className="space-y-2">
        <button
          onClick={handleConfirm}
          disabled={isLoading}
          className="w-full rounded-lg bg-accent px-4 py-2.5 text-sm font-medium text-accent-text shadow-sm hover:bg-accent-hover focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface focus:outline-none disabled:opacity-50"
        >
          {isLoading ? "Signing in..." : "Sign in here, end other session"}
        </button>

        <button
          onClick={onCancel}
          disabled={isLoading}
          className="w-full rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-surface-hover hover:text-text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface focus:outline-none disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
